/**
 * AtlasXRModule — Quest-class VR support (workstream 6, Meta Quest 3 target).
 *
 * Wraps the engine `XRModule` with the Atlas-specific glue:
 *  - An "Enter VR" HUD button, revealed once XR capabilities detect a VR runtime
 *    (Quest browser; desktop link too, when the renderer booted WebGL2).
 *  - Rig adaptation across the session boundary: the desktop rig parks EYE_HEIGHT
 *    on the camera, but `local-floor` supplies REAL head height — so the offset
 *    (and any mouse pitch) zeroes on sessionstart and restores on sessionend,
 *    else the player floats an extra 1.6 m above the ground.
 *  - Locomotion (Input-phase system, active only while presenting), driven by
 *    the player's COMFORT choices (lib/xr_comfort.ts `comfort()` — the VR panel's
 *    Comfort page writes them; the rules are pure in lib/xr_locomotion.ts):
 *      MOVE stick  → (the move hand; left by default, `moveHand` swaps)
 *                    slide: HEAD-relative move intent into atlas.intentX/Z — the
 *                    exact server-validated `move` path the keyboard uses (Hard
 *                    Rule #1); teleport: aim an arc, let go to hop — the hop is
 *                    the server-validated `xrTeleport` reducer, paid from the
 *                    movement budget, under a blink (vr/TeleportController).
 *      TURN stick  → snap (`snapDeg`, latched) or smooth (`smoothDegPerS`, with a
 *                    deadzone) applied to atlas.localYaw; the headset pose
 *                    composes on top of the body yaw.
 *      A button    → jump intent on the press edge (mirrors Space keydown).
 *  - Comfort (XR P2, 2026-09-28): a head-locked VIGNETTE that narrows with the
 *    rig's ARTIFICIAL speed and turn rate (stronger riding), the blink, the seated
 *    floor offset + height nudge + recenter as an OFFSET REFERENCE SPACE
 *    (vr/XrRigSpace — the head pose stays real), `requestXrRecenter()` for the UI.
 *
 * Controller state is read straight off `session.inputSources` (xr-standard
 * mapping: axes[2,3] = thumbstick, buttons[4] = A/X) — Atlas owns its raw input
 * exactly like AtlasInputModule owns the DOM; the engine InputSystem is not
 * registered in this app, so XRInputPollSystem's action-map route is idle here.
 *
 * Quest 3 serving note: WebXR needs a secure context. On-device, use
 * `adb reverse tcp:5180 tcp:5180` and browse http://localhost:5180 (localhost
 * is secure), or serve the client over HTTPS on the LAN.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { XRModule } from 'enginex/xr/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { EYE_HEIGHT } from './AtlasAvatarModule.js';
import { padFeedXr, padClearXr } from '../lib/gamepad.js';
import { comfort, floorOffsetM, setComfort, type XRComfort } from '../lib/xr_comfort.js';
import { wallAt } from '../lib/structure_masks.js';
import { waterStillAt } from '../lib/water_fx.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { terrainHeight } from '../../../shared/terrain.js';
import { settlementSites } from '../../../shared/settlements.js';
import {
  stickOf, stickRoles, snapTurn, smoothTurn, headRelativeIntent, rigMotion, blinkLevel, takeXrRecenter,
  BLINK_OUT_S, BLINK_IN_S, STICK_DEADZONE, type SnapLatch, type RigPose, type BlinkPhase,
} from '../lib/xr_locomotion.js';
import { ComfortVignette } from './vr/VignetteMesh.js';
import { spaceComfortOn, sectorRigMotionNow } from '../lib/space_comfort.js';
import { TeleportController, type TeleportPhysics } from './vr/TeleportController.js';
import { XrRigSpace, type XrManagerLike } from './vr/XrRigSpace.js';

/** Vite substitutes `import.meta.env.DEV` (true on the dev server, false in a build); the cast keeps
 *  tsc happy without Vite's client types. */
const XR_DEV_BUILD = (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true;

/** A comfort lever for probes and the UI: the module's live state (see `state()`), and the probe's hands. */
declare global { interface Window { __xrLoco?: () => unknown; __xrLocoDo?: Record<string, (...a: never[]) => unknown> } }

export class AtlasXRModule implements IModule {
  readonly name = 'AtlasXR';

  private app!: App;
  private button: HTMLButtonElement | null = null;
  private readonly snap: SnapLatch = { armed: true };
  private prevJump = false;
  private sessionStartAt = 0;
  private lastLocoLog = 0;
  private readonly dir = new THREE.Vector3();
  // ── Comfort (XR P2, 2026-09-28) ──
  private vignette: ComfortVignette | null = null;
  private teleport: TeleportController | null = null;
  private rigSpace: XrRigSpace | null = null;
  private physicsRef: TeleportPhysics | null | undefined = undefined;
  private prevRig: RigPose | null = null;
  /** Yaw applied as discrete steps (snap turns) since the vignette last measured — never a turn rate. */
  private steppedYaw = 0;
  /** The recenter's own blink: out, the recenter at black, in. */
  private recPhase: BlinkPhase = 'idle';
  private recT = 0;
  private recFromUi = false;
  private readonly diag = { snaps: 0, lastSnapDeg: 0, turnDeg: 0, turnDt: 0, turnRateDps: 0, moveHand: 'left', turnMode: 'snap', moveMode: 'slide', slide: 0, speedMps: 0, yawRateDps: 0, riding: false, recenters: 0, systemResets: 0, lastRecenterYawDeg: 0 };

  constructor(private readonly xr: XRModule) {}

  init(app: App): void {
    this.app = app;

    const xrm = app.renderer.native.xr;
    xrm.addEventListener('sessionstart', () => this.onSessionStart());
    xrm.addEventListener('sessionend', () => this.onSessionEnd());

    // Locomotion poll — Input phase, so the intent is in place before the
    // FixedSim movement system (AtlasMovement) integrates + sends it.
    app.world.addSystem(makeSystem('AtlasXRLocomotion', Phase.Input, 20, (dt) => this.poll(dt)));
    // Comfort — RenderSub, after the avatar (Animation/36) and the vehicles (Animation/38) moved the rig this frame:
    // measure the rig's ARTIFICIAL motion, ease the vignette, draw the blink. Inert outside a session.
    app.world.addSystem(makeSystem('AtlasXRComfort', Phase.RenderSub, 40, (dt) => this.comfortFrame(dt)));
    window.__xrLoco = () => this.state();
    window.__xrLocoDo = this.probeLevers() as unknown as Record<string, (...a: never[]) => unknown>;
    // VR screenshots — UI phase (after the render, same XR frame): one full
    // readPixels of the XR framebuffer every SHOT interval, downsampled and
    // POSTed to the dev collector (/xr-shot → gallery at :9224/xr-shots).
    // "See what the headset sees" without devtools. ONE bulk read per
    // interval (a single pipeline sync ≈ one dropped frame) replaces the old
    // per-2s strip probe whose repeated 1-row reads were a per-capture stall
    // storm — the "glitchy when I tilt my head" hitch suspect.
    // ?xrshot=<seconds> overrides the cadence; ?xrshot=0 disables.
    // DEV BUILDS ONLY by default (bug sweep 2026-09-22): every production VR session paid a
    // full-framebuffer readPixels (a GPU sync — a dropped frame in the headset) every 8 s, a JPEG
    // encode and a POST to a collector that only the dev stack runs. The params are parsed ONCE
    // (URLSearchParams was rebuilt every frame).
    {
      const q = new URLSearchParams(location.search);
      const shot = q.get('xrshot');
      this.xrShotSecs = shot !== null ? Number(shot) : XR_DEV_BUILD ? 8 : 0;
      this.xrShotHalf = Number(q.get('xrshotres') ?? 4) === 2;
      const diag = q.get('xrdiag');
      this.xrDiag = diag !== null ? diag !== '0' : XR_DEV_BUILD;
    }
    app.world.addSystem(makeSystem('AtlasXRShot', Phase.UI, 50, () => this.xrScreenshot()));

    // Capabilities resolve during app.start(); world-entered fires well after
    // (a server round-trip), so the button gates correctly without polling.
    // Gate on WORLD entry, not account-ready: account-ready fires while the
    // DOM main menu is still up, and a session started there has no world and
    // no avatar rig — in-headset that is a black void (DOM never renders in
    // immersive sessions) with the controllers lost for the whole session.
    // ALSO gate on the loading screen: fixedMovement ignores intent while
    // atlas.loadingGate is up, so a session started mid-load is an unbuilt
    // world where snap turn works but the sticks don't move you (the Quest 3
    // "can turn but can't move, no buildings" field report, 2026-08-15).
    bus.on('atlas:world-entered', () => { if (!atlas.loadingGate) this.buildButton(); });
    bus.on('atlas:loading-done', () => this.buildButton());
  }

  // ─── Session boundary ─────────────────────────────────────────────────────────

  private onSessionStart(): void {
    atlas.xrPresenting = true;
    this.sessionStartAt = performance.now();
    this.snap.armed = true;
    this.prevJump = false;
    this.prevRig = null;
    this.steppedYaw = 0;
    this.recPhase = 'idle';
    // The seated floor offset + height nudge ride an offset reference space (the head pose stays real).
    this.rigSpace ??= new XrRigSpace(this.app.renderer.native.xr as unknown as XrManagerLike);
    this.rigSpace.onReset = () => this.onSystemReset();
    this.rigSpace.start(floorOffsetM(comfort()));
    // local-floor provides real head height on the rig child; the parked
    // desktop eye offset + mouse pitch would double-apply.
    const cam = atlas.localMesh?.children[0];
    if (cam) {
      cam.position.y = 0;
      cam.rotation.x = 0;
    }
    if (this.button) this.button.textContent = 'Exit VR';
    bus.emit('atlas:status', { text: 'VR session started' });
  }

  private onSessionEnd(): void {
    atlas.xrPresenting = false;
    padClearXr();          // hand the snapshot back to the desktop poll
    // THE STALE-SPACE LAW: three keeps a custom reference space past the session — clear it, or the next session
    // reads a dead session's space. The vignette, the arc and any blink go with the session.
    this.rigSpace?.end();
    this.vignette?.detach();
    this.teleport?.reset();
    this.recPhase = 'idle';
    this.prevRig = null;

    atlas.intentX = 0;
    atlas.intentZ = 0;
    const cam = atlas.localMesh?.children[0];
    if (cam) cam.position.y = EYE_HEIGHT;
    if (this.button) this.button.textContent = 'Enter VR';
    // A session that dies in under 3 s is the dark-flash failure — NAME it
    // (a headset has no console; silent failure must be impossible).
    const lived = performance.now() - this.sessionStartAt;
    if (this.sessionStartAt > 0 && lived < 3000) {
      const gl = (this.app.renderer.native as unknown as { backend?: { isWebGLBackend?: boolean } }).backend?.isWebGLBackend;
      bus.emit('atlas:status', {
        text: `VR ended after ${Math.round(lived)}ms — backend ${gl ? 'WebGL2 (expected)' : 'WebGPU (XR needs WebGL2 — reload the page)'}`,
      });
    } else {
      bus.emit('atlas:status', { text: 'VR session ended' });
    }
    this.sessionStartAt = 0;
  }

  // ─── Locomotion (only while presenting) ───────────────────────────────────────

  private poll(dt: number): void {
    const xrm = this.app.renderer.native.xr;
    if (!xrm.isPresenting) return;
    const session = xrm.getSession();
    if (!session) return;
    const c = comfort();
    const roles = stickRoles(c.moveHand);

    // THE ACTIONS COME THROUGH THE SHARED PAD LAYER (2026-09-16). The headset's
    // controllers are not in `navigator.getGamepads()` — they hang off
    // `XRInputSource.gamepad` — so they are fed in and arrive as an ordinary
    // snapshot. That way the same actions and the same REBINDS work in the
    // headset as on the desk, and locomotion below stays the XR module's own.
    // The move hand rides along: it is the hand that steers a ride, so a wall scrape buzzes it (the haptics table, lib/gamepad.ts).
    padFeedXr(Array.from(session.inputSources) as unknown as Parameters<typeof padFeedXr>[0], c.moveHand);

    let moveX = 0;
    let moveY = 0;
    let turnX = 0;
    let jump = false;
    let moveSource: XRInputSource | null = null;

    for (const source of session.inputSources) {
      const gp = source.gamepad;
      if (!gp) continue;
      // xr-standard: axes[2,3] = thumbstick (Quest); a 2-axis runtime puts the stick at [0,1] — `stickOf` falls back
      // so the stick is never silently dead on a nonstandard mapping (and reads a NULL axis as 0).
      const st = stickOf(gp.axes as unknown as ArrayLike<number | null>);
      // The comfort page's MOVE HAND swaps which stick walks and which turns.
      if (source.handedness === roles.move) { moveX = st.x; moveY = st.y; moveSource = source; }
      else if (source.handedness === roles.turn) turnX = st.x;
      if (source.handedness === 'right') jump = gp.buttons[4]?.pressed ?? false; // A (the right controller) jumps
    }
    const s = moveX;
    const f = -moveY; // stick Y is DOWN-positive

    // In-headset locomotion diagnostics, 1 Hz: a devtools-less device phones
    // these home through the beacon console ring. Field law: turn-works-but-
    // no-movement class bugs are undebuggable without per-source axis proof.
    if (this.xrDiag && performance.now() - this.lastLocoLog > 1000) { // dev builds / ?xrdiag=1 (the 1 Hz phone-home)
      this.lastLocoLog = performance.now();
      // Axes arrays can carry null entries (unused touchpad slots on some
      // runtimes/emulators) — null.toFixed crashed the WHOLE poll before
      // intent was written: telemetry must never kill locomotion.
      const srcs = Array.from(session.inputSources).map((src) =>
        `${src.handedness}${src.hand ? '+hand' : ''}:${src.gamepad ? Array.from(src.gamepad.axes, (a) => (typeof a === 'number' ? a.toFixed(2) : String(a))).join(',') : 'no-gp'}`);
      console.info(`[XRLoco] f=${f.toFixed(2)} s=${s.toFixed(2)} turn=${turnX.toFixed(2)} presenting=${atlas.xrPresenting} sources=[${srcs.join(' | ')}]`);
      // [XRView] — the compositor-facing truth for the eye-misalignment class:
      // session visibility (blurred = inputs suppressed by the UA), which
      // layer type is attached, its pixel dimensions, and each eye camera's
      // viewport + position. A devtools-less headset phones these home.
      try {
        const rs = session.renderState as XRRenderState & { layers?: unknown[] };
        const base = rs.baseLayer ?? null;
        const nLayers = rs.layers?.length ?? -1;
        const xrm2 = this.app.renderer.native.xr as unknown as { getCamera?: () => { cameras: Array<{ viewport?: { x: number; y: number; width: number; height: number }; position: { x: number; y: number; z: number } }> } };
        const cams = xrm2.getCamera?.()?.cameras ?? [];
        const vps = cams.map((c, i) => {
          const v = c.viewport;
          const p = c.position;
          return `e${i}=${v ? `${v.x},${v.y},${v.width}x${v.height}` : 'no-vp'}@(${p.x.toFixed(3)},${p.y.toFixed(3)},${p.z.toFixed(3)})`;
        });
        const baseDims = base ? `${(base as XRWebGLLayer).framebufferWidth ?? '?'}x${(base as XRWebGLLayer).framebufferHeight ?? '?'}` : 'none';
        // Renderer-side sizing: canvas target dims × pixelRatio + the stored
        // renderer viewport — a mismatch against the layer dims is the
        // renderer-drew-too-small bug class, directly.
        const rn = this.app.renderer.native as unknown as {
          getSize(t: unknown): { x: number; y: number };
          getViewport(t: unknown): { x: number; y: number; z: number; w: number };
          getPixelRatio(): number;
          domElement?: { width: number; height: number };
        };
        // three's getters copy into the target via .set/.copy — hand them
        // real vectors (THREE.Vector2/4), not bare objects.
        const sz = rn.getSize(new THREE.Vector2());
        const vp2 = rn.getViewport(new THREE.Vector4()) as unknown as { x: number; y: number; z: number; w: number };
        console.info(
          `[XRView] vis=${session.visibilityState} layers=${nLayers} base=${base ? base.constructor?.name : 'null'}:${baseDims} ` +
          `rsize=${sz.x}x${sz.y}@pr${rn.getPixelRatio()} canvas=${rn.domElement?.width}x${rn.domElement?.height} ` +
          `rvp=${vp2.x},${vp2.y},${vp2.z}x${vp2.w} ${vps.join(' ')}`);
      } catch (err) {
        console.info(`[XRView] probe failed: ${(err as Error)?.message}`);
      }
      // [XRSettle] — the settlement-assembly truth from inside the session
      // ("structures never load in — VR only" field report 2026-08-16): the
      // nearest sites, what's built vs mid-assembly, and each build stage.
      try {
        const diag = (window as unknown as { __settlements?: () => { sites: number; near: Array<{ id: number; tier: string; d: number }>; built: number; building: number[]; stage: Array<[number, string]>; queue: number; catalog?: number } }).__settlements?.();
        if (diag) {
          const near = diag.near.map((n) => `${n.tier}#${n.id}@${n.d}m`).join(',');
          const stages = diag.stage.map(([id, st]) => `${id}:${st}`).join(',');
          console.info(`[XRSettle] sites=${diag.sites} near=[${near}] built=${diag.built} building=[${diag.building.join(',')}] stage=[${stages}] q=${diag.queue} cat=${diag.catalog ?? '?'}`);
        } else {
          console.info('[XRSettle] no diag hook');
        }
      } catch { /* diag shape drift — silent */ }
    }

    // A recenter asked by the UI (requestXrRecenter): the blink starts here, the recenter happens at black.
    if (takeXrRecenter() && this.recPhase === 'idle') { this.recPhase = 'out'; this.recT = 0; this.recFromUi = true; }
    // The seated floor offset + height nudge follow the comfort store (cheap when unchanged).
    this.rigSpace?.apply(floorOffsetM(c));

    const riding = atlas.hoverRide || atlas.skyFlight || atlas.spaceFlight;
    // TURNING: snap (one flick = one step) or smooth (a rate × the stick past a deadzone).
    this.diag.turnMode = c.turn;
    this.diag.moveHand = c.moveHand;
    this.diag.moveMode = c.move;
    let dYaw = 0;
    // On a hovercraft the rig's yaw IS the hull's heading and the ride steers it from the move stick (the vehicle
    // module's padRideFromSnapshot) — a snap there would jump the craft 30° mid-drive (XR P2.16). The head still looks.
    // Space campaign L (2026-10-01): in the SECTOR the turn stick flies the ship's nose (lib/space_xr_flight) — a snap
    // there turned `localYaw` (the sector never reads it; the avatar landed facing elsewhere) and counted a turn.
    const sectorFlies = atlas.spaceFlight && spaceComfortOn();
    const turnIn = atlas.hoverRide || sectorFlies ? 0 : turnX;
    if (c.turn === 'snap') {
      dYaw = snapTurn(this.snap, turnIn, c.snapDeg);
      if (dYaw !== 0) { this.steppedYaw += dYaw; this.diag.snaps++; this.diag.lastSnapDeg = +(dYaw * 180 / Math.PI).toFixed(2); }
    } else {
      this.snap.armed = true;
      dYaw = smoothTurn(turnIn, c.smoothDegPerS, dt);
    }
    if (dYaw !== 0) atlas.localYaw += dYaw; // stick right → dYaw < 0 → the body turns right (the old latch's sign)
    this.diag.turnDeg = +(dYaw * 180 / Math.PI).toFixed(3);
    this.diag.turnDt = +Math.min(dt, 0.1).toFixed(4);
    this.diag.turnRateDps = c.turn === 'smooth' && dt > 0 ? +Math.abs(dYaw * 180 / Math.PI / Math.min(dt, 0.1)).toFixed(1) : 0;

    // Jump on the press edge (intentJump is consumed+cleared by FixedSim).
    if (jump && !this.prevJump && !sectorFlies) atlas.intentJump = true; // in the sector A levels the wings
    this.prevJump = jump;

    // TELEPORT: the move stick aims; walking stands down while the hop owns the moment. Where a hop cannot happen
    // (a vehicle, indoors, afloat) the stick slides, so the player is never stranded.
    const phys = this.physics();
    const indoors = atlas.interiorId !== null || atlas.caveId !== null || atlas.spaceInterior;
    const swimming = phys ? (phys.waterMode === 'swim' || phys.waterMode === 'dive') : false;
    const teleportOn = c.move === 'teleport' && !riding && !indoors && !swimming && !atlas.flyMode;
    const tp = (this.teleport ??= this.makeTeleport());
    if (teleportOn || tp.busy) {
      tp.update(Math.min(dt, 0.25), {
        stickY: teleportOn ? moveY : 0,
        frame: xrm.getFrame?.() ?? null,
        refSpace: xrm.getReferenceSpace(),
        source: moveSource,
        physics: phys,
        riding, indoors, swimming,
      }, (x, z) => this.sendHop(x, z));
      atlas.intentX = 0;
      atlas.intentZ = 0;
      this.diag.slide = 0;
      return;
    }
    tp.arc.hide();

    // SLIDE: head-relative movement. WebXRManager composes the headset pose onto the rig-child camera, so its world
    // −Z is where the player LOOKS; project to XZ and reuse the desktop intent convention (forward = (−sin, −cos)·yaw).
    if (Math.hypot(s, f) < STICK_DEADZONE) {
      atlas.intentX = 0;
      atlas.intentZ = 0;
      this.diag.slide = 0;
      return;
    }
    // Head-relative when the rig camera is reachable; otherwise fall back to the avatar yaw. The old `if (!cam)
    // return` bailed WITHOUT writing intent — a missing rig child silently killed all stick movement while snap turn
    // (which writes localYaw directly) kept working.
    const cam = atlas.localMesh?.children[0];
    let yawEff = atlas.localYaw;
    if (cam) {
      cam.getWorldDirection(this.dir);
      if (Math.hypot(this.dir.x, this.dir.z) > 1e-4) {
        yawEff = Math.atan2(-this.dir.x, -this.dir.z);
      }
    }
    const it = headRelativeIntent(moveX, moveY, yawEff);
    atlas.intentX = it.x;
    atlas.intentZ = it.z;
    this.diag.slide = +Math.hypot(it.x, it.z).toFixed(3);
  }

  // ─── Comfort: the vignette, the blink, the recenter (only while presenting) ──────

  private comfortFrame(dt: number): void {
    const xrm = this.app.renderer.native.xr;
    if (!xrm.isPresenting) return;
    const c = comfort();
    const cam = atlas.localMesh?.children[0];
    const rig = atlas.localMesh;
    const v = (this.vignette ??= new ComfortVignette());
    if (cam) v.attach(cam);
    // The recenter's blink: out → recenter at black → in.
    let recBlack = 0;
    if (this.recPhase !== 'idle') {
      this.recT += Math.min(dt, 0.25);
      if (this.recPhase === 'out' && this.recT >= BLINK_OUT_S) {
        this.doRecenter();
        this.recPhase = 'in'; this.recT = 0;
      } else if (this.recPhase === 'in' && this.recT >= BLINK_IN_S) {
        this.recPhase = 'idle'; this.recT = 0;
      }
      recBlack = blinkLevel(this.recPhase, this.recT);
    }
    // The rig's ARTIFICIAL motion (its own displacement and yaw — never the head's physical movement). A snap turn,
    // a teleport or a correction is a discontinuity, not a speed.
    const riding = atlas.hoverRide || atlas.skyFlight || atlas.spaceFlight;
    let motion: { speedMps: number; yawRateDps: number; riding: boolean } | null = null;
    if (rig) {
      const cur: RigPose = { x: rig.position.x, y: rig.position.y, z: rig.position.z, yaw: rig.rotation.y };
      const m = rigMotion(this.prevRig, cur, dt, this.steppedYaw);
      this.prevRig = cur;
      this.steppedYaw = 0;
      const busy = this.teleport?.busy ?? false;
      motion = m.jump || busy ? null : { speedMps: m.speedMps, yawRateDps: m.yawRateDps, riding };
      // Space campaign L (2026-10-01): in the SECTOR the rig's raw speed is the wrong cue (a 105 m/s cruise held the
      // view at its narrowest; anything past 400 m/s read as a "jump"; `rotation.y` of a 3-D attitude is no turn rate).
      // There the vignette follows the SHIP's acceleration and body-axis rotation (lib/space_comfort, the comfort
      // page's "closes on" choice) — never the head, never the speed.
      if (atlas.spaceFlight && spaceComfortOn()) motion = busy ? null : sectorRigMotionNow(c.spaceCue);
      this.diag.speedMps = +m.speedMps.toFixed(2);
      this.diag.yawRateDps = +m.yawRateDps.toFixed(1);
      this.diag.riding = riding;
    }
    const black = Math.max(recBlack, this.teleport?.black() ?? 0);
    v.update(dt, motion, c, black);
    // The teleport arc lives in the scene (absolute coordinates — the rebase law).
    const tp = this.teleport;
    if (tp && !tp.arc.group.parent) this.app.world.scene.add(tp.arc.group);
  }

  /** The app recenter (inside the XR frame): the head's floor position and heading become the origin. On foot the
   *  body turns by the head's old heading so the WORLD view does not jump — only the physical offset is taken up;
   *  riding, the vehicle's heading is left alone (the seat comes in front of you). */
  private doRecenter(): void {
    const before = this.rigSpace?.recenter();
    if (before === null || before === undefined) return;
    this.diag.recenters++;
    this.diag.lastRecenterYawDeg = +(before * 180 / Math.PI).toFixed(2);
    const riding = atlas.hoverRide || atlas.skyFlight || atlas.spaceFlight;
    if (!riding && !atlas.flyMode) atlas.localYaw += before;
    this.prevRig = null;
    if (this.recFromUi) bus.emit('atlas:status', { text: 'Recentered' });
    this.recFromUi = false;
  }

  /** The system recentered (the reference space's `reset`): a short blink in, and no motion across the jump. */
  private onSystemReset(): void {
    this.diag.systemResets++;
    this.prevRig = null;
    this.recPhase = 'in';
    this.recT = 0;
  }

  private physics(): TeleportPhysics | null {
    if (this.physicsRef === undefined) {
      try { this.physicsRef = (this.app.getModule('PhysicsModule') as unknown as TeleportPhysics | null) ?? null; } catch { this.physicsRef = null; }
    }
    return this.physicsRef ?? null;
  }

  private makeTeleport(): TeleportController {
    const tp = new TeleportController();
    this.app.world.scene.add(tp.arc.group);
    return tp;
  }

  /** The hop's server call — the SERVER moves the row (Hard Rule #1); the client's reconcile follows it. */
  private sendHop(x: number, z: number): Promise<void> {
    const conn = atlas.conn;
    const r = conn?.reducers as unknown as { xrTeleport?: (a: { x: number; z: number }) => Promise<void> } | undefined;
    if (!r || typeof r.xrTeleport !== 'function') return Promise.reject(new Error('Teleport is not available on this server'));
    return r.xrTeleport({ x, z });
  }

  /** The probe's hands (verify/xr-locomotion-probe.mjs): comfort, the camera's local pose, spots to test the hop's
   *  refusals against (the server's own rules: the analytic terrain vs the derived sea, the wall masks). */
  private probeLevers(): Record<string, unknown> {
    const seed = (): string | null => {
      const conn = atlas.conn;
      if (!conn || atlas.commonsWorldId === null) return null;
      return conn.db.world.worldId.find(atlas.commonsWorldId)?.terrainSeed ?? null;
    };
    return {
      comfort: (patch: Partial<XRComfort>) => setComfort(patch),
      camLocal: () => { const c = atlas.localMesh?.children[0]; return c ? { x: c.position.x, y: c.position.y, z: c.position.z } : null; },
      setCamLocal: (x: number, y: number, z: number) => { const c = atlas.localMesh?.children[0]; if (c) c.position.set(x, y, z); return !!c; },
      recenter: () => { if (this.recPhase === 'idle') { this.recPhase = 'out'; this.recT = 0; this.recFromUi = true; } return true; },
      /** A point 2–4.8 m from the player the server calls sea (ground ≤ sea level + 0.5 − a margin). */
      findSea: (maxM = 4.8) => {
        const sd = seed(); if (!sd) return null;
        const sea = commonsSeaLevel(sd);
        for (let r = 2; r <= maxM; r += 0.4) for (let k = 0; k < 48; k++) {
          const a = (k / 48) * Math.PI * 2, x = atlas.localX + Math.sin(a) * r, z = atlas.localZ + Math.cos(a) * r;
          if (terrainHeight(sd, x, z) <= sea + 0.2) return { x, z, r, water: waterStillAt(x, z) };
        }
        return null;
      },
      /** A sea spot the server calls water (the analytic ground ≥ 0.3 m under the derived sea level) near the player
       *  (≤ 60 m), and the highest spot 3.8 m from it (the beach side) to stand on. The server judges the TARGET. */
      findSeaHop: (radiusM = 60) => {
        const sd = seed(); if (!sd) return null;
        const sea = commonsSeaLevel(sd);
        for (let r = 0; r <= radiusM; r += 2) for (let k = 0; k < Math.max(1, Math.round(r * 2)); k++) {
          const a = (k / Math.max(1, Math.round(r * 2))) * Math.PI * 2;
          const sx = atlas.localX + Math.sin(a) * r, sz = atlas.localZ + Math.cos(a) * r;
          if (terrainHeight(sd, sx, sz) > sea - 0.3) continue;
          let best: { lx: number; lz: number; h: number } | null = null;
          for (let j = 0; j < 16; j++) {
            const b = (j / 16) * Math.PI * 2, lx = sx + Math.sin(b) * 3.8, lz = sz + Math.cos(b) * 3.8;
            const h = terrainHeight(sd, lx, lz);
            if (!best || h > best.h) best = { lx, lz, h };
          }
          if (best) return { lx: best.lx, lz: best.lz, landAboveSeaM: +(best.h - sea).toFixed(2), sx, sz, seaLevel: sea };
        }
        return null;
      },
      /** The nearest settlement's centre (a town has walls to test a hop against). */
      nearestTown: () => {
        const sd = seed(); if (!sd) return null;
        let best: { x: number; z: number; d: number; tier: string } | null = null;
        for (const t of settlementSites(sd)) {
          if (t.tier !== 'city' && t.tier !== 'town' && t.tier !== 'hamlet') continue;
          const d = Math.hypot(t.centerX - atlas.localX, t.centerZ - atlas.localZ);
          if (!best || d < best.d) best = { x: t.centerX, z: t.centerZ, d, tier: t.tier };
        }
        return best;
      },
      /** Two free points ~4.4 m apart with a wall cell between them (the walking-height masks), within `radiusM` of
       *  (cx, cz) (default: the player). */
      findWallCrossing: (radiusM = 40, cx?: number, cz?: number) => {
        const ox = cx ?? atlas.localX, oz = cz ?? atlas.localZ;
        const dirs = [[1, 0], [0, 1], [0.7071, 0.7071], [0.7071, -0.7071]];
        const freeAround = (x: number, z: number): boolean => { for (const [dx, dz] of [[0, 0], [0.5, 0], [-0.5, 0], [0, 0.5], [0, -0.5]]) if (wallAt(x + dx, z + dz)) return false; return true; };
        for (let r = 0; r <= radiusM; r += 1) for (let k = 0; k < Math.max(1, Math.round(r * 6)); k++) {
          const a = (k / Math.max(1, Math.round(r * 6))) * Math.PI * 2;
          const wx = ox + Math.sin(a) * r, wz = oz + Math.cos(a) * r;
          if (!wallAt(wx, wz)) continue;
          for (const [ux, uz] of dirs) {
            const ax = wx - ux * 2.2, az = wz - uz * 2.2, bx = wx + ux * 2.2, bz = wz + uz * 2.2;
            if (freeAround(ax, az) && freeAround(bx, bz)) return { ax, az, bx, bz, wx, wz };
          }
        }
        return null;
      },
    };
  }

  /** Everything a probe or the UI may read (window.__xrLoco()). */
  state(): Record<string, unknown> {
    const c = comfort();
    return {
      presenting: atlas.xrPresenting,
      comfort: { ...c },
      floorOffsetM: floorOffsetM(c),
      localYawDeg: +(atlas.localYaw * 180 / Math.PI).toFixed(2),
      intent: { x: +atlas.intentX.toFixed(3), z: +atlas.intentZ.toFixed(3) },
      ...this.diag,
      vignette: this.vignette?.state() ?? null,
      teleport: this.teleport?.state() ?? null,
      rigSpace: this.rigSpace?.state() ?? null,
      recenterPhase: this.recPhase,
      customSpace: (() => { try { const xr = this.app.renderer.native.xr as unknown as { _customReferenceSpace?: unknown }; return !!xr._customReferenceSpace; } catch { return null; } })(),
    };
  }

  // ─── VR screenshots (see-what-the-headset-sees) ───────────────────────────────

  private lastShot = 0;
  private shotInFlight = false;
  private xrShotSecs = 0;
  private xrShotHalf = false;
  private xrDiag = false;

  /** Capture the XR base-layer framebuffer AFTER the render and POST a
   *  downsampled JPEG to the dev collector (/xr-shot; gallery at
   *  :9224/xr-shots). ONE bulk readPixels per interval — a single pipeline
   *  sync (~one dropped frame) instead of the old strip probe's stall storm.
   *  Cadence: ?xrshot=<seconds> (default 8; 0 disables). */
  private xrScreenshot(): void {
    const xrm = this.app.renderer.native.xr;
    if (!xrm.isPresenting || this.shotInFlight) return;
    const secs = this.xrShotSecs;
    if (!(secs > 0)) return;
    const now = performance.now();
    if (now - this.lastShot < secs * 1000) return;
    this.lastShot = now;
    try {
      const session = xrm.getSession();
      const base = session?.renderState.baseLayer;
      if (!base) return;
      const gl = (this.app.renderer.native as unknown as { getContext?: () => WebGL2RenderingContext }).getContext?.();
      if (!gl) return;
      const w = base.framebufferWidth, h = base.framebufferHeight;
      const prevRead = gl.getParameter(gl.READ_FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, base.framebuffer);
      const full = new Uint8Array(w * h * 4);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, full);
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, prevRead);
      // Downsample /4 + flip (GL y-up → image y-down) off the readback.
      // ?xrshotres=2 halves instead of quarters (texture-detail forensics:
      // /4 thumbnails cannot distinguish textured-white from flat-white).
      const shift = this.xrShotHalf ? 1 : 2;
      const dw = w >> shift, dh = h >> shift;
      const img = new ImageData(dw, dh);
      // Stride follows the shift (the hardcoded ×4 at half-res wrapped rows:
      // four ghost copies + a black bottom in the first hi-res captures).
      const step = 1 << shift;
      for (let y = 0; y < dh; y++) {
        const sy = (h - 1 - y * step) * w;
        for (let x = 0; x < dw; x++) {
          const s = (sy + x * step) * 4;
          const o = (y * dw + x) * 4;
          img.data[o] = full[s];
          img.data[o + 1] = full[s + 1];
          img.data[o + 2] = full[s + 2];
          img.data[o + 3] = 255;
        }
      }
      const cv = document.createElement('canvas');
      cv.width = dw; cv.height = dh;
      cv.getContext('2d')?.putImageData(img, 0, 0);
      this.shotInFlight = true;
      cv.toBlob((blob) => {
        if (!blob) { this.shotInFlight = false; return; }
        fetch('/xr-shot', { method: 'POST', body: blob, headers: { 'Content-Type': 'image/jpeg' } })
          .catch(() => { /* collector down — fine */ })
          .finally(() => { this.shotInFlight = false; });
      }, 'image/jpeg', 0.65);
    } catch {
      this.shotInFlight = false;
    }
  }

  // ─── Session button ───────────────────────────────────────────────────────────

  private buildButton(): void {
    if (this.button || !this.xr.capabilities.vrSupported) return;
    const b = document.createElement('button');
    b.textContent = 'Enter VR';
    // bottom:64 — the round "?" help button owns the 14px corner at z90 and
    // sat directly ON TOP of this button (the Quest overlap report).
    b.style.cssText =
      'position:fixed;right:12px;bottom:64px;z-index:40;padding:10px 16px;' +
      'background:var(--ax-sel);color:var(--ax-text);border:1px solid var(--ax-accent);border-radius:var(--ax-radius);' +
      'font:600 14px var(--ax-font);cursor:pointer;';
    b.onclick = () => {
      void (this.xr.isPresenting ? this.xr.endSession() : this.xr.requestSession('immersive-vr'))
        .catch((err: unknown) => {
          bus.emit('atlas:status', { text: `VR session failed: ${String(err).slice(0, 120)}` });
        });
    };
    document.body.appendChild(b);
    this.button = b;
  }

  dispose(): void {
    this.button?.remove();
    this.button = null;
  }
}
